import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Phone, Mail, Pin, User, Search, Upload, ChevronLeft, ChevronRight, Settings2, Building2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";

const EMPTY = { name: "", title: "", phone: "", email: "", notes: "", district_id: "", contact_type: "" };
const DEPT_TYPES = ["Police", "Maintenance", "Transportation", "IT", "Custodial"];

function DistrictSelect({ value, onChange, districts }) {
  return (
    <div className="space-y-2">
      <Label>District</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="contact-district-select"><SelectValue placeholder="Select a district" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="contact-district-option-all">All Districts</SelectItem>
          {districts.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`contact-district-option-${d.id}`}>{d.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ContactTypeField({ value, onChange }) {
  return (
    <div className="space-y-2">
      <Label>Contact type</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="e.g. Police" maxLength={60} data-testid="contact-type-input" />
      <div className="flex flex-wrap gap-1.5" data-testid="contact-type-examples">
        {DEPT_TYPES.map((t) => (
          <button key={t} type="button" onClick={() => onChange(t)} data-testid={`contact-type-chip-${t.toLowerCase()}`}
            className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${value === t ? "border-teal-600 bg-teal-600 text-white" : "border-slate-200 text-slate-600 hover:border-teal-300 hover:bg-teal-50"}`}>{t}</button>
        ))}
      </div>
    </div>
  );
}

function ContactDialog({ open, setOpen, editing, endpoint, onDone, districts, dept = false, defaultDistrict = "" }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (editing) setForm({ name: editing.name || "", title: editing.title || "", phone: editing.phone || "", email: editing.email || "", notes: editing.notes || "", district_id: editing.district_id || "", contact_type: editing.contact_type || "" });
    else setForm({ ...EMPTY, district_id: dept ? (defaultDistrict || "all") : "" });
  }, [editing, open, dept, defaultDistrict]);

  const submit = async () => {
    if (dept && !form.contact_type.trim()) { toast.error("Enter a contact type (e.g. Police)"); return; }
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (districts && !form.district_id) { toast.error("Please select a district"); return; }
    const { contact_type, ...rest } = form;
    const payload = dept ? { ...form, kind: "district_dept" } : rest;
    setLoading(true);
    try {
      if (editing) { await api.put(`${endpoint}/${editing.id}`, payload); toast.success("Contact updated"); }
      else { await api.post(endpoint, payload); toast.success("Contact added"); }
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const heading = dept ? (editing ? "Edit District Dept Contact" : "New District Dept Contact") : (editing ? "Edit contact" : "New contact");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid={dept ? "dept-contact-dialog" : "contact-dialog"}>
        <DialogHeader><DialogTitle className="font-heading">{heading}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {dept && <ContactTypeField value={form.contact_type} onChange={set("contact_type")} />}
          {districts && <DistrictSelect value={form.district_id} onChange={set("district_id")} districts={districts} />}
          <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name")(e.target.value)} data-testid="contact-name-input" /></div>
          <div className="space-y-2"><Label>Title / Role</Label><Input value={form.title} onChange={(e) => set("title")(e.target.value)} placeholder={dept ? "e.g. Dispatch Supervisor" : "e.g. Athletic Director"} data-testid="contact-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone")(e.target.value)} data-testid="contact-phone-input" /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={(e) => set("email")(e.target.value)} data-testid="contact-email-input" /></div>
          </div>
          <div className="space-y-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="contact-notes-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} data-testid="contact-submit-btn" className="rounded-full bg-slate-900 hover:bg-slate-800">{editing ? "Save" : "Add contact"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactCard({ c, endpoint, onEdit, onDelete }) {
  const dept = c.kind === "district_dept";
  return (
    <Card className={`p-4 border-slate-200 shadow-sm ${c.scope === "global" ? "bg-amber-50/50 border-amber-200" : dept ? "bg-teal-50/40 border-teal-200" : ""}`} data-testid={`contact-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {c.scope === "global" && <Pin className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
          <div className="min-w-0">
            {dept && c.contact_type && <p className="mb-1 inline-block rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" data-testid={`contact-type-label-${c.id}`}>{c.contact_type}</p>}
            <p className="font-semibold text-slate-800 truncate">{c.name}</p>
            {c.title && <p className="text-xs font-medium text-slate-500">{c.title}</p>}
            {c.district_name && <p className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${dept ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"}`} data-testid={`contact-district-label-${c.id}`}>{c.district_name}</p>}
          </div>
        </div>
        {c.editable && (
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(c)} data-testid={`contact-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => onDelete(c)} data-testid={`contact-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        )}
      </div>
      <div className="mt-3 space-y-1.5 text-sm">
        {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-sky-600"><Phone className="h-3.5 w-3.5" /> {c.phone}</a>}
        {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-slate-600 hover:text-sky-600 truncate"><Mail className="h-3.5 w-3.5 shrink-0" /> {c.email}</a>}
      </div>
      {(c.guardian_name || c.guardian_phone || c.guardian_email) && (
        <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-2.5 text-sm" data-testid={`contact-guardian-${c.id}`}>
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Parent / Guardian</p>
          {c.guardian_name && <p className="font-medium text-slate-700">{c.guardian_name}</p>}
          {c.guardian_phone && <a href={`tel:${c.guardian_phone}`} className="flex items-center gap-2 text-slate-600 hover:text-sky-600"><Phone className="h-3.5 w-3.5" /> {c.guardian_phone}</a>}
          {c.guardian_email && <a href={`mailto:${c.guardian_email}`} className="flex items-center gap-2 text-slate-600 hover:text-sky-600 truncate"><Mail className="h-3.5 w-3.5 shrink-0" /> {c.guardian_email}</a>}
        </div>
      )}
      {c.notes && <p className="mt-2 text-xs text-slate-400">{c.notes}</p>}
    </Card>
  );
}

function parseCSV(text) {
  const rows = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return rows;
  const splitLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (ch === "," && !inQ) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const KEYS = ["name", "phone", "email", "title", "notes"];
  const header = splitLine(lines[0]).map((h) => h.toLowerCase());
  const hasHeader = KEYS.some((k) => header.includes(k));
  const cols = hasHeader ? header : KEYS;
  const start = hasHeader ? 1 : 0;
  for (let i = start; i < lines.length; i++) {
    const vals = splitLine(lines[i]);
    const rec = {};
    cols.forEach((c, idx) => { if (KEYS.includes(c)) rec[c] = vals[idx] || ""; });
    if (rec.name) rows.push(rec);
  }
  return rows;
}

function ImportDialog({ open, setOpen, endpoint, onDone, districts }) {
  const [text, setText] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [loading, setLoading] = useState(false);
  const parsed = parseCSV(text);

  useEffect(() => { if (!open) { setText(""); setDistrictId(""); } }, [open]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setText(ev.target.result || "");
    reader.readAsText(file);
  };

  const submit = async () => {
    if (parsed.length === 0) { toast.error("No valid contacts found. Include a 'name' for each row."); return; }
    if (districts && !districtId) { toast.error("Please select a district"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`${endpoint}/import`, { contacts: parsed, district_id: districtId || undefined });
      toast.success(`Imported ${data.imported} contact${data.imported === 1 ? "" : "s"}`);
      setText(""); setDistrictId(""); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="import-contacts-dialog">
        <DialogHeader><DialogTitle className="font-heading">Import contacts</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {districts && (
            <div className="space-y-2">
              <Label>District</Label>
              <Select value={districtId} onValueChange={setDistrictId}>
                <SelectTrigger data-testid="import-district-select"><SelectValue placeholder="Select a district" /></SelectTrigger>
                <SelectContent>
                  {districts.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`import-district-option-${d.id}`}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <p className="text-sm text-slate-500">Upload a CSV or paste rows below. Columns: <span className="font-mono text-xs">name, phone, email, title, notes</span> (a header row is optional; <span className="font-semibold">name</span> is required).</p>
          <Input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="import-file-input" />
          <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={"name,phone,email,title,notes\nJane Smith,210-555-0101,jane@example.com,Judge,Prefers email"} data-testid="import-textarea" className="font-mono text-xs" />
          {text.trim() && <p className="text-xs font-semibold text-slate-600" data-testid="import-preview-count">{parsed.length} valid contact{parsed.length === 1 ? "" : "s"} detected</p>}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading || parsed.length === 0} data-testid="import-submit-btn" className="rounded-full bg-slate-900 hover:bg-slate-800">Import {parsed.length > 0 ? `(${parsed.length})` : ""}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Phonebook({ endpoint, title, subtitle, districts, deptDistricts, defaultDeptDistrict = "", pageSize = 8, requestHeaders, onLocked }) {
  const [pinned, setPinned] = useState([]);
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [pinnedPage, setPinnedPage] = useState(1);
  const [deptPage, setDeptPage] = useState(1);
  const [memberPage, setMemberPage] = useState(1);
  const [myPage, setMyPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint, requestHeaders ? { headers: requestHeaders } : undefined);
      setPinned(data.pinned || []);
      setMembers(data.members || []);
      setDepartments(data.departments || []);
      setItems(data.items || []);
    } catch (err) {
      const code = err.response?.status;
      if ((code === 401 || code === 423) && onLocked) { onLocked(code); return; }
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, [endpoint, requestHeaders, onLocked]);
  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    try { await api.delete(`${endpoint}/${del.id}`); toast.success("Contact deleted"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  const match = (c) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [c.name, c.title, c.contact_type, c.phone, c.email, c.notes, c.district_name, c.guardian_name, c.guardian_phone, c.guardian_email].some((v) => (v || "").toLowerCase().includes(s));
  };
  const filteredPinned = pinned.filter(match);
  const filteredDepts = departments.filter(match);
  const filteredMembers = members.filter(match);
  const filteredItems = items.filter(match);
  const all = [...pinned, ...departments, ...members, ...items];
  const noResults = q.trim() && filteredPinned.length === 0 && filteredDepts.length === 0 && filteredMembers.length === 0 && filteredItems.length === 0;

  const PINNED_SIZE = 2;
  const MY_SIZE = pageSize;
  const pinnedPages = Math.ceil(filteredPinned.length / PINNED_SIZE) || 1;
  const deptPages = Math.ceil(filteredDepts.length / MY_SIZE) || 1;
  const memberPages = Math.ceil(filteredMembers.length / MY_SIZE) || 1;
  const myPages = Math.ceil(filteredItems.length / MY_SIZE) || 1;
  const curPinned = Math.min(pinnedPage, pinnedPages);
  const curDept = Math.min(deptPage, deptPages);
  const curMember = Math.min(memberPage, memberPages);
  const curMy = Math.min(myPage, myPages);
  const pinnedSlice = filteredPinned.slice((curPinned - 1) * PINNED_SIZE, curPinned * PINNED_SIZE);
  const deptSlice = filteredDepts.slice((curDept - 1) * MY_SIZE, curDept * MY_SIZE);
  const memberSlice = filteredMembers.slice((curMember - 1) * MY_SIZE, curMember * MY_SIZE);
  const mySlice = filteredItems.slice((curMy - 1) * MY_SIZE, curMy * MY_SIZE);
  const editContact = (x) => { setEditing(x); if (x.kind === "district_dept") setDeptOpen(true); else setOpen(true); };

  const Pager = ({ page, pages, onPage, testId }) => pages <= 1 ? null : (
    <div className="mt-4 flex items-center justify-center gap-3" data-testid={testId}>
      <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testId}-prev`}><ChevronLeft className="h-4 w-4" /></Button>
      <span className="text-sm font-medium text-slate-600 tabular-nums">{page} / {pages}</span>
      <Button variant="outline" size="sm" className="rounded-full" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testId}-next`}><ChevronRight className="h-4 w-4" /></Button>
    </div>
  );

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}
        actions={<div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowSettings((s) => !s)} variant="outline" className="rounded-full" data-testid="phonebook-settings-btn"><Settings2 className="mr-2 h-4 w-4" /> Settings</Button>
          {deptDistricts && <Button onClick={() => { setEditing(null); setDeptOpen(true); }} variant="outline" className="rounded-full border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="new-dept-contact-btn"><Building2 className="mr-2 h-4 w-4" /> New District Dept Contact</Button>}
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-contact-btn"><Plus className="mr-2 h-4 w-4" /> New contact</Button>
        </div>} />

      {showSettings && (
        <div className="mb-6" data-testid="phonebook-settings-panel">
          <Button onClick={() => setImportOpen(true)} variant="outline" className="rounded-full" data-testid="import-contacts-btn"><Upload className="mr-2 h-4 w-4" /> Import</Button>
        </div>
      )}

      {all.length > 0 && (
        <div className="relative mb-6 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, title, district, phone or email…" className="pl-9" data-testid="phonebook-search" />
        </div>
      )}

      {noResults && <EmptyState title="No matches" description={`No contacts match "${q}".`} testId="phonebook-no-results" />}

      {filteredPinned.length > 0 && (
        <>
          <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-amber-600"><Pin className="h-4 w-4" /> District Numbers</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="pinned-contacts">
            {pinnedSlice.map((c) => <ContactCard key={c.id} c={c} endpoint={endpoint} />)}
          </div>
          <Pager page={curPinned} pages={pinnedPages} onPage={setPinnedPage} testId="pinned-pagination" />
          <div className="mb-8" />
        </>
      )}

      {filteredDepts.length > 0 && (
        <>
          <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-teal-700"><Building2 className="h-4 w-4" /> District Dept Contacts</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="dept-contacts">
            {deptSlice.map((c) => <ContactCard key={c.id} c={c} endpoint={endpoint} onEdit={editContact} onDelete={(x) => setDel(x)} />)}
          </div>
          <Pager page={curDept} pages={deptPages} onPage={setDeptPage} testId="dept-pagination" />
          <div className="mb-8" />
        </>
      )}

      {filteredMembers.length > 0 && (
        <>
          <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-sky-600"><User className="h-4 w-4" /> Team Member Contacts</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="member-contacts">
            {memberSlice.map((c) => <ContactCard key={c.id} c={c} endpoint={endpoint} />)}
          </div>
          <Pager page={curMember} pages={memberPages} onPage={setMemberPage} testId="member-pagination" />
          <div className="mb-8" />
        </>
      )}

      {(pinned.length > 0 || members.length > 0 || departments.length > 0) && !noResults && <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500"><User className="h-4 w-4" /> My contacts</h3>}
      {noResults ? null : items.length === 0 ? (
        all.length === 0
          ? <EmptyState title="No contacts yet" description="Add your first contact to build your phonebook." testId="phonebook-empty" />
          : <p className="text-sm text-slate-400" data-testid="phonebook-empty">No personal contacts yet.</p>
      ) : filteredItems.length === 0 ? (
        <p className="text-sm text-slate-400">No personal contacts match your search.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="my-contacts">
            {mySlice.map((c) => <ContactCard key={c.id} c={c} endpoint={endpoint} onEdit={editContact} onDelete={(x) => setDel(x)} />)}
          </div>
          <Pager page={curMy} pages={myPages} onPage={setMyPage} testId="my-pagination" />
        </>
      )}

      <ContactDialog open={open} setOpen={setOpen} editing={open ? editing : null} endpoint={endpoint} onDone={load} districts={districts} />
      {deptDistricts && <ContactDialog open={deptOpen} setOpen={setDeptOpen} editing={deptOpen ? editing : null} endpoint={endpoint} onDone={load} districts={deptDistricts} dept defaultDistrict={defaultDeptDistrict} />}
      <ImportDialog open={importOpen} setOpen={setImportOpen} endpoint={endpoint} onDone={load} districts={districts} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete contact?"
        description={del ? `"${del.name}" will be removed from your phonebook.` : ""}
        confirmText="Delete" destructive onConfirm={doDelete} testId="delete-contact-dialog" />
    </div>
  );
}
